import React from 'react';
import { Form, Formik } from 'formik';
import { GlobalRulesOverviewOverallSchema } from '@brighterion/types/overview';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import useUpdateOverallThreshold from 'Hooks/query-hooks/global-rules/useUpdateOverallThreshold';
import { configService } from 'Services';
import { Levels } from 'Common/enums';
import { FormikInputField } from 'Components/FormikComponents';
import { Icon, Modal } from '@brighterion/br-ui';
import useUserConfig from 'Hooks/query-hooks/useUserConfig';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import { useTranslation } from 'react-i18next';
import { overallThresholdSchema } from '../globalRulesOverviewSchema';

import styles from './GlobalRulesOverviewOverallForm.css';

type GlobalRulesOverviewOverallFormProps = {
    initialFormValues: GlobalRulesOverviewOverallSchema;
    closeModal: () => void;
};

const GlobalRulesOverviewOverallForm = ({ initialFormValues, closeModal }: GlobalRulesOverviewOverallFormProps) => {
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { getUserInfo } = useAccessTokenStore.getState();
    const uniqueUserTokenId = getUserInfo().tenantKey;
    const userConfig = useUserConfig(uniqueUserTokenId);
    const overallThresholdMutation = useUpdateOverallThreshold({
        templateRepositoryId: templateRepositoryId!,
        level: Levels.ACQUIRER
    });
    const { t } = useTranslation();
    const maxThreshold = configService.constants.maxGlobalAggregateThreshold;

    const initialValues: GlobalRulesOverviewOverallSchema = {
        description: initialFormValues.description,
        accept: initialFormValues.accept
    };

    if (userConfig.isLoading || userConfig.isFetching) {
        return <RouteLoading />;
    }

    if (userConfig.isError) {
        const { errorCmp } = errorController([userConfig.error]);
        return errorCmp;
    }

    return (
        <div>
            <Formik
                enableReinitialize
                data-testid='edit-overview-form'
                initialValues={initialValues}
                validationSchema={overallThresholdSchema(maxThreshold)}
                onSubmit={async (values, actions) => {
                    overallThresholdMutation.mutate(values, {
                        onSuccess: closeModal,
                        onError: () => actions.setSubmitting(false)
                    });
                }}
            >
                {({ dirty, handleChange, handleBlur, isSubmitting, handleSubmit, isValid }) => {
                    return (
                        <Form>
                            <div className={styles.modalBody}>
                                <FormikInputField
                                    className={styles.thresholdDescription}
                                    type='text'
                                    label={t('globalOverview.form.description')}
                                    name='description'
                                    handleChange={handleChange}
                                    handleBlur={handleBlur}
                                />
                                <div className={styles.thresholdContainer}>
                                    <label htmlFor='accept' className={styles.thresholdScoreLabel}>
                                        {t('globalOverview.form.aggregateScoringThreshold')}
                                    </label>
                                    <div className={styles.thresholdStatement}>
                                        <div className={styles.thresholdConclusion}>
                                            <Icon name='accept' size='md' className={styles.icon} aria-label='accept' />
                                            Accept
                                        </div>
                                        <div className={styles.thresholdScoreLeq}>{'If score <='}</div>
                                        <div>
                                            <FormikInputField
                                                id='accept'
                                                type='number'
                                                name='accept'
                                                handleChange={handleChange}
                                                handleBlur={handleBlur}
                                                min={0}
                                                max={maxThreshold}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <Modal.Footer
                                id='edit-overview-footer'
                                onClose={closeModal}
                                onActionText={t('Save')}
                                disableOnAction={
                                    !dirty || !isValid || isSubmitting || overallThresholdMutation.isLoading
                                }
                                onAction={handleSubmit}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default GlobalRulesOverviewOverallForm;
